import { AlertCircle, Calendar, Edit, Eye, FolderOpen, Lock, Mic, Plus, Trash2, TrendingUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { getImageUrl } from '../../services/axiosConfig.ts';
import blogAPI from '../../services/blogAPI';
import Dashboard from './blog/Dashboard';
import TagsManagement from './blog/TagsManagement';
import BlogCategoryModal from './BlogCategoryModal';
import './BlogManagement.css';
import BlogPostDetailModal from './BlogPostDetailModal';
import BlogPostEditor from './BlogPostEditor';
import PodcastGenerationModal from './PodcastGenerationModal';
import ScheduledPostsManagement from './ScheduledPostsManagement';
import SchedulePostModal from './SchedulePostModal';

const BlogManagement = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  const [usageStats, setUsageStats] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [newTag, setNewTag] = useState('');
  const [editForm, setEditForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    status: 'draft',
    categories: [],
    tags: []
  });

  useEffect(() => {
    loadData();
  }, [statusFilter, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const queryParams = { page: 1, limit: 100 };
      if (statusFilter && statusFilter !== 'all') {
        queryParams.status = statusFilter;
      }

      const [postsRes, categoriesRes, statsRes] = await Promise.all([
        blogAPI.getAllPosts(queryParams).catch(err => {
          console.error('Posts error:', err);
          return { data: { posts: [], total: 0 } };
        }),
        blogAPI.getAllCategories().catch(err => {
          console.error('Categories error:', err);
          return { data: { success: false, categories: [] } };
        }),
        blogAPI.getUsageStats().catch(err => {
          console.error('Stats error:', err);
          return { data: { stats: null } };
        })
      ]);

      console.log('Categories response:', categoriesRes.data);
      setPosts(postsRes.data.posts || []);
      setCategories(categoriesRes.data.categories || []);
      setUsageStats(statsRes.data.stats || null);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load blog data. Please check your connection and try again.');
      setPosts([]);
      setCategories([]);
      setUsageStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePodcast = async (options) => {
    if (!selectedPost) return;

    try {
      const response = await blogAPI.generatePodcast(selectedPost._id, options);
      alert('Podcast generated successfully!');

      // Update the selected post with the fresh data that includes the podcast
      if (response.data.post) {
        setSelectedPost(response.data.post);
      }

      // Reload to get updated post data in the list
      await loadData();

      setShowPodcastModal(false);
    } catch (error) {
      console.error('Error generating podcast:', error);
      throw error;
    }
  };

  const handleCreatePost = () => {
    setSelectedPost(null);
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setSelectedPost(post);
    setShowEditor(true);
  };

  const handleViewPost = (post) => {
    setSelectedPost(post);
    setShowDetailModal(true);
  };

  const handleSavePost = async (postData) => {
    try {
      if (selectedPost && selectedPost._id) {
        // Update existing post
        await blogAPI.updatePost(selectedPost._id, postData);
        alert('Post updated successfully!');
      } else {
        // Create new post
        const response = await blogAPI.createPost(postData);
        console.log('Post created:', response.data);
        alert('Post created successfully!');
      }
      setShowEditor(false);
      setSelectedPost(null);
      loadData();
    } catch (error) {
      console.error('Error saving post:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save post';
      alert(`Failed to save post: ${errorMessage}`);
      throw error;
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await blogAPI.deletePost(postId);
      console.log('Delete response:', response);
      alert('Post deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting post:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete post';
      alert(`Failed to delete post: ${errorMessage}`);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName, postCount) => {
    if (postCount > 0) {
      alert(`Cannot delete "${categoryName}". It is used in ${postCount} blog post${postCount > 1 ? 's' : ''}. Please reassign these posts first.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) return;

    try {
      await blogAPI.deleteCategory(categoryId);
      alert('Category deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete category';
      alert(`Failed to delete category: ${errorMessage}`);
    }
  };

  // Get all unique tags from posts
  const allTags = [...new Set(posts.flatMap(post => post.tags || []))];

  // Calculate stats
  const totalViews = posts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
  const totalComments = posts.reduce((sum, post) => sum + (post.comments?.length || 0), 0);
  const publishedPosts = posts.filter(p => p.status === 'published').length;
  const draftPosts = posts.filter(p => p.status === 'draft').length;
  const scheduledPosts = posts.filter(p => p.status === 'scheduled');
  const engagementRate = posts.length > 0 ? ((totalLikes + totalComments) / totalViews * 100).toFixed(1) : 0;

  if (loading) {
    return <div className="blog-loading">Loading blog management...</div>;
  }

  return (
    <div className="blog-management">
      <div className="blog-management-container">
        {/* Header Section */}
        <div className="blog-management-header">
          <h1 className="blog-management-title">Blog Management</h1>
          <p className="blog-management-subtitle">Create and manage blog posts with AI assistance</p>
        </div>

        {/* Tab Navigation */}
        <div className="blog-tabs">
          <button
            className={`blog-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            Posts
          </button>
          <button
            className={`blog-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
          <button
            className={`blog-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`blog-tab ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            Tags
          </button>
          <button
            className={`blog-tab ${activeTab === 'scheduling' ? 'active' : ''}`}
            onClick={() => setActiveTab('scheduling')}
          >
            <Lock size={16} /> Scheduling
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-banner">
            <AlertCircle /> {error}
          </div>
        )}

        {/* POSTS TAB */}
        {activeTab === 'posts' && (
          <>
            {/* Toolbar with Actions */}
            <div className="blog-toolbar">
              <div className="blog-filters">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <button onClick={handleCreatePost} className="btn-primary">
                <Plus size={18} /> New Post
              </button>
            </div>

            {/* Stats Cards */}
            <div className="usage-stats-card">
              <div className="stat-card">
                <div className="stat-card-header">
                  <div className="stat-icon primary">
                    <FolderOpen />
                  </div>
                </div>
                <div className="stat-value">{posts.length}</div>
                <div className="stat-label">Total Posts</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header">
                  <div className="stat-icon success">
                    <Eye />
                  </div>
                </div>
                <div className="stat-value">{publishedPosts}</div>
                <div className="stat-label">Published</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header">
                  <div className="stat-icon warning">
                    <Edit />
                  </div>
                </div>
                <div className="stat-value">{draftPosts}</div>
                <div className="stat-label">Drafts</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header">
                  <div className="stat-icon primary">
                    <TrendingUp />
                  </div>
                </div>
                <div className="stat-value">{totalViews}</div>
                <div className="stat-label">Total Views</div>
              </div>
            </div>

            {/* Post Editor */}
            {showEditor && (
              <BlogPostEditor
                post={selectedPost}
                categories={categories}
                onSave={handleSavePost}
                onClose={() => setShowEditor(false)}
              />
            )}

            {/* Posts List */}
            <div className="posts-grid">
              {posts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <FolderOpen size={64} />
                  </div>
                  <h3 className="empty-state-title">No posts yet</h3>
                  <p className="empty-state-text">Create your first blog post to get started</p>
                  <button onClick={handleCreatePost} className="btn-primary">
                    <Plus size={18} /> Create First Post
                  </button>
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post._id} className="post-card">
                    {(post.featuredImage || post.coverImage) && (
                      <img
                        className="post-card-image"
                        src={getImageUrl(
                          post.coverImage ||
                          (typeof post.featuredImage === 'string' ? post.featuredImage : post.featuredImage?.url) ||
                          ''
                        )}
                        alt={post.title}
                      />
                    )}
                    <div className="post-card-content">
                      <div className="post-card-header">
                        <h3 className="post-card-title">{post.title}</h3>
                        <span className={`post-status-badge ${post.status}`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="post-card-excerpt">{post.excerpt}</p>
                      <div className="post-card-meta">
                        <span className="post-card-meta-item">
                          <Eye size={14} /> {post.views || 0} views
                        </span>
                        <span className="post-card-meta-item">
                          <Calendar size={14} /> {new Date(post.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="post-card-actions">
                        <button
                          onClick={() => handleViewPost(post)}
                          className="post-action-btn"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleEditPost(post)}
                          className="post-action-btn"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPost(post);
                            setShowPodcastModal(true);
                          }}
                          className="post-action-btn"
                          title="Generate Podcast"
                        >
                          <Mic size={18} />
                        </button>
                        <button
                          onClick={() => handleDeletePost(post._id)}
                          className="post-action-btn delete"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* CATEGORIES TAB */}
        {activeTab === 'categories' && (
          <>
            <div className="blog-toolbar">
              <div className="blog-filters">
                {/* Placeholder for future filters */}
              </div>
              <button onClick={() => setShowCategoryManager(true)} className="btn-primary">
                <Plus size={18} /> New Category
              </button>
            </div>
            <div className="categories-grid">
              {categories.length === 0 ? (
                <div className="empty-state">
                  <h3 className="empty-state-title">No blog categories yet</h3>
                  <p className="empty-state-text">Create categories to organize your blog posts</p>
                </div>
              ) : (
                categories.map(cat => (
                  <div
                    key={cat._id}
                    className="category-card"
                    style={{ borderLeftColor: cat.color || '#3b82f6' }}
                  >
                    <div className="category-content">
                      <div className="category-name">{cat.name}</div>
                      <div className="category-count">{cat.postCount || 0} posts</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(cat._id, cat.name, cat.postCount || 0);
                      }}
                      className="category-delete-btn"
                      title="Delete category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* TAGS TAB */}
        {activeTab === 'tags' && (
          <>
            {!showTagManager ? (
              <>
                <div className="blog-toolbar">
                  <div className="blog-filters">
                    {/* Placeholder for future filters */}
                  </div>
                  <button onClick={() => setShowTagManager(true)} className="btn-primary">
                    <Plus size={18} /> New Tag
                  </button>
                </div>
                <div className="empty-state">
                  <h3 className="empty-state-title">Tags</h3>
                  <p className="empty-state-text">Manage your post tags here</p>
                </div>
              </>
            ) : (
              <TagsManagement
                showNotification={(type, message) => alert(message)}
              />
            )}
          </>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <Dashboard
            stats={{
              totalPosts: posts.length,
              publishedPosts: posts.filter(p => p.status === 'published').length,
              draftPosts: posts.filter(p => p.status === 'draft').length,
              scheduledPosts: posts.filter(p => p.status === 'scheduled').length,
              categories: categories.length,
              tags: allTags.length,
              totalViews: totalViews,
              recentPosts: posts.slice(0, 5)
            }}
            onRefresh={loadData}
          />
        )}

        {/* SCHEDULING TAB */}
        {activeTab === 'scheduling' && (
          <>
            <div className="blog-toolbar">
              <div className="blog-filters">
                {/* Placeholder for future filters */}
              </div>
              <button onClick={() => setShowScheduleModal(true)} className="btn-primary">
                <Plus size={18} /> New Schedule
              </button>
            </div>
            <ScheduledPostsManagement key={scheduleRefreshKey} />
          </>
        )}

        {/* Modals */}
        {showEditor && (
          <BlogPostEditor
            post={selectedPost}
            categories={categories}
            onSave={handleSavePost}
            onClose={() => {
              setShowEditor(false);
              setSelectedPost(null);
            }}
          />
        )}

        {showDetailModal && selectedPost && (
          <BlogPostDetailModal
            post={selectedPost}
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedPost(null);
            }}
            onEdit={() => {
              setShowDetailModal(false);
              setShowEditor(true);
            }}
            onDelete={handleDeletePost}
            onUpdate={loadData}
          />
        )}

        {showPodcastModal && selectedPost && (
          <PodcastGenerationModal
            isOpen={showPodcastModal}
            post={selectedPost}
            onGenerate={handleGeneratePodcast}
            onClose={() => {
              setShowPodcastModal(false);
              setSelectedPost(null);
            }}
          />
        )}

        {showScheduleModal && (
          <SchedulePostModal
            isOpen={showScheduleModal}
            onClose={() => setShowScheduleModal(false)}
            onSuccess={() => {
              setShowScheduleModal(false);
              setScheduleRefreshKey(prev => prev + 1); // Force refresh of ScheduledPostsManagement
              loadData();
            }}
          />
        )}

        {showCategoryManager && (
          <BlogCategoryModal
            isOpen={showCategoryManager}
            onClose={() => setShowCategoryManager(false)}
            onSuccess={() => {
              setShowCategoryManager(false);
              loadData(); // Refresh categories list
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BlogManagement;
